import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/design-system.css';
import LandingPage from './components/LandingPage';
import PDFAIEditor from './components/PDFAIEditor';
import FormDetection from './components/FormDetection';
import InvoiceExtraction from './components/InvoiceExtraction';
import BulkProcessing from './components/BulkProcessing';
import MergePDF from './components/tools/MergePDF';
import SplitPDF from './components/tools/SplitPDF';
import CompressPDF from './components/tools/CompressPDF';
import RotatePDF from './components/tools/RotatePDF';
import JPGtoPDF from './components/tools/JPGtoPDF';
import PDFtoJPG from './components/tools/PDFtoJPG';
import RemovePages from './components/tools/RemovePages';
import ExtractPages from './components/tools/ExtractPages';
import PDFMetadataEditor from './components/tools/PDFMetadataEditor';
import OrganizePDF from './components/tools/OrganizePDF';
import ScanToPDF from './components/tools/ScanToPDF';
import RepairPDF from './components/tools/RepairPDF';
import OCRPDF from './components/tools/OCRPDF';
import WORDtoPDF from './components/tools/WORDtoPDF';
import HTMLtoPDF from './components/tools/HTMLtoPDF';
import POWERPOINTtoPDF from './components/tools/POWERPOINTtoPDF';
import EXCELtoPDF from './components/tools/EXCELtoPDF';
import PDFtoWORD from './components/tools/PDFtoWORD';
import PDFtoPOWERPOINT from './components/tools/PDFtoPOWERPOINT';
import PDFtoEXCEL from './components/tools/PDFtoEXCEL';
import PDFtoPDFA from './components/tools/PDFtoPDFA';
import PDFtoMarkdown from './components/tools/PDFtoMarkdown';
import AddPageNumbers from './components/tools/AddPageNumbers';
import AddWatermark from './components/tools/AddWatermark';
import ProtectPDF from './components/tools/ProtectPDF';
import UnlockPDF from './components/tools/UnlockPDF';
import CropPDF from './components/tools/CropPDF';
import SignPDF from './components/tools/SignPDF';
import PDFtoText from './components/tools/PDFtoText';
import ComparePDF from './components/tools/ComparePDF';
import PDFPageCounter from './components/tools/PDFPageCounter';
import AnnotatePDF from './components/tools/AnnotatePDF';
import FillPDFForms from './components/tools/FillPDFForms';
import RedactPDF from './components/tools/RedactPDF';
import ValidatePDF from './components/tools/ValidatePDF';
import AISummarizer from './components/tools/AISummarizer';
import AIChatPDF from './components/tools/AIChatPDF';
import AITranslator from './components/tools/AITranslator';
import {
  Edit, FileCheck, Receipt, FolderOpen,
  Merge, Scissors, Minimize2, FileImage,
  Lock, Unlock, Eye, Menu, X, RotateCw, Trash2, FileOutput, Info,
  FileText, Shield, Layers, ChevronDown, Grid3x3, Sparkles, Zap, Scan, Wrench, Globe, Presentation, FileSpreadsheet,
  Hash, Droplet, Crop, PenTool, MessageSquare, Edit3, EyeOff, CheckCircle, Languages, Bot, FileCode
} from 'lucide-react';

// Tool Categories with enhanced metadata
const toolCategories = [
  {
    id: 'organize',
    title: 'ORGANIZE PDF',
    description: 'Merge, split, and manage your PDF pages',
    color: '#E63946',
    gradient: 'linear-gradient(135deg, #E63946 0%, #FF6B6B 100%)',
    tools: [
      { id: 'merge', label: 'Merge PDF', icon: Merge, component: MergePDF, description: 'Combine multiple PDFs into one' },
      { id: 'split', label: 'Split PDF', icon: Scissors, component: SplitPDF, description: 'Separate pages into multiple files' },
      { id: 'remove-pages', label: 'Remove pages', icon: Trash2, component: RemovePages, description: 'Delete unwanted pages' },
      { id: 'extract-pages', label: 'Extract pages', icon: FileOutput, component: ExtractPages, description: 'Extract specific pages' },
      { id: 'organize', label: 'Organize PDF', icon: Layers, component: OrganizePDF, description: 'Reorder and arrange pages' },
      { id: 'scan', label: 'Scan to PDF', icon: Scan, component: ScanToPDF, description: 'Convert scans to PDF' },
    ]
  },
  {
    id: 'optimize',
    title: 'OPTIMIZE PDF',
    description: 'Compress and enhance your PDFs',
    color: '#10B981',
    gradient: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
    tools: [
      { id: 'compress', label: 'Compress PDF', icon: Minimize2, component: CompressPDF, description: 'Reduce file size' },
      { id: 'repair', label: 'Repair PDF', icon: Wrench, component: RepairPDF, description: 'Fix corrupted PDFs' },
      { id: 'ocr', label: 'OCR PDF', icon: Eye, component: OCRPDF, description: 'Make PDFs searchable' },
    ]
  },
  {
    id: 'convert-to',
    title: 'CONVERT TO PDF',
    description: 'Transform any file into PDF format',
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
    tools: [
      { id: 'jpg-to-pdf', label: 'JPG to PDF', icon: FileImage, component: JPGtoPDF, description: 'Convert images to PDF' },
      { id: 'word-to-pdf', label: 'WORD to PDF', icon: FileText, component: WORDtoPDF, description: 'Convert DOCX to PDF' },
      { id: 'powerpoint-to-pdf', label: 'POWERPOINT to PDF', icon: Presentation, component: POWERPOINTtoPDF, description: 'Convert PPTX to PDF' },
      { id: 'excel-to-pdf', label: 'EXCEL to PDF', icon: FileSpreadsheet, component: EXCELtoPDF, description: 'Convert XLSX to PDF' },
      { id: 'html-to-pdf', label: 'HTML to PDF', icon: Globe, component: HTMLtoPDF, description: 'Convert web pages to PDF' },
    ]
  },
  {
    id: 'convert-from',
    title: 'CONVERT FROM PDF',
    description: 'Export PDFs to other formats',
    color: '#3B82F6',
    gradient: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)',
    tools: [
      { id: 'pdf-to-jpg', label: 'PDF to JPG', icon: FileImage, component: PDFtoJPG, description: 'Convert to images' },
      { id: 'pdf-to-word', label: 'PDF to WORD', icon: FileText, component: PDFtoWORD, description: 'Convert to DOCX' },
      { id: 'pdf-to-powerpoint', label: 'PDF to POWERPOINT', icon: Presentation, component: PDFtoPOWERPOINT, description: 'Convert to PPTX' },
      { id: 'pdf-to-excel', label: 'PDF to EXCEL', icon: FileSpreadsheet, component: PDFtoEXCEL, description: 'Convert to XLSX' },
      { id: 'pdf-to-markdown', label: 'PDF to Markdown', icon: FileCode, component: PDFtoMarkdown, description: 'Convert to MD' },
      { id: 'pdf-to-pdfa', label: 'PDF to PDF/A', icon: Shield, component: PDFtoPDFA, description: 'Archive format' },
    ]
  },
  {
    id: 'edit',
    title: 'EDIT PDF',
    description: 'Modify and enhance your PDFs',
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
    tools: [
      { id: 'edit', label: 'Edit PDF', icon: Edit, component: PDFAIEditor, description: 'AI-powered editing' },
      { id: 'rotate', label: 'Rotate PDF', icon: RotateCw, component: RotatePDF, description: 'Rotate pages' },
      { id: 'add-page-numbers', label: 'Add Page Numbers', icon: Hash, component: AddPageNumbers, description: 'Number your pages' },
      { id: 'add-watermark', label: 'Add Watermark', icon: Droplet, component: AddWatermark, description: 'Add watermarks' },
      { id: 'crop', label: 'Crop PDF', icon: Crop, component: CropPDF, description: 'Crop pages' },
      { id: 'annotate', label: 'Annotate PDF', icon: MessageSquare, component: AnnotatePDF, description: 'Add comments' },
      { id: 'fill-forms', label: 'Fill PDF Forms', icon: Edit3, component: FillPDFForms, description: 'Fill form fields' },
      { id: 'metadata', label: 'Edit Metadata', icon: Info, component: PDFMetadataEditor, description: 'Edit PDF info' },
    ]
  },
  {
    id: 'security',
    title: 'PDF SECURITY',
    description: 'Protect and secure your documents',
    color: '#EF4444',
    gradient: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)',
    tools: [
      { id: 'protect', label: 'Protect PDF', icon: Lock, component: ProtectPDF, description: 'Add password' },
      { id: 'unlock', label: 'Unlock PDF', icon: Unlock, component: UnlockPDF, description: 'Remove password' },
      { id: 'sign', label: 'Sign PDF', icon: PenTool, component: SignPDF, description: 'Digital signature' },
      { id: 'redact', label: 'Redact PDF', icon: EyeOff, component: RedactPDF, description: 'Remove sensitive info' },
      { id: 'validate', label: 'Validate PDF', icon: CheckCircle, component: ValidatePDF, description: 'Check integrity' },
      { id: 'compare', label: 'Compare PDF', icon: FileCheck, component: ComparePDF, description: 'Compare documents' },
    ]
  },
  {
    id: 'ai-tools',
    title: 'AI-POWERED TOOLS',
    description: 'Advanced AI features for your PDFs',
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
    tools: [
      { id: 'ai-summarizer', label: 'AI Summarizer', icon: Sparkles, component: AISummarizer, description: 'Summarize PDF with AI' },
      { id: 'ai-chat', label: 'AI Chat with PDF', icon: Bot, component: AIChatPDF, description: 'Chat with your PDF' },
      { id: 'ai-translator', label: 'AI Translator', icon: Languages, component: AITranslator, description: 'Translate PDF to any language' },
    ]
  },
  {
    id: 'additional',
    title: 'ADDITIONAL TOOLS',
    description: 'Extra utilities and features',
    color: '#06B6D4',
    gradient: 'linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)',
    tools: [
      { id: 'pdf-to-text', label: 'PDF to Text', icon: FileText, component: PDFtoText, description: 'Extract text' },
      { id: 'page-counter', label: 'PDF Info', icon: Info, component: PDFPageCounter, description: 'View PDF details' },
      { id: 'form-detection', label: 'Form Detection', icon: Receipt, component: FormDetection, description: 'Detect forms' },
      { id: 'invoice-extraction', label: 'Invoice Extraction', icon: Receipt, component: InvoiceExtraction, description: 'Extract invoices' },
      { id: 'bulk-processing', label: 'Bulk Processing', icon: FolderOpen, component: BulkProcessing, description: 'Process multiple files' },
    ]
  },
];

// Enhanced Dashboard Home Component
function DashboardHome({ onSelectTool }) {
  const [hoveredCategory, setHoveredCategory] = useState(null);

  return (
    <div style={{
      padding: 'var(--spacing-10) var(--spacing-6)',
      maxWidth: '1400px',
      margin: '0 auto',
      animation: 'fadeIn 0.5s ease-out'
    }}>
      {/* Hero Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: 'var(--spacing-12)',
        position: 'relative'
      }}>
        {/* Decorative gradient background */}
        <div style={{
          position: 'absolute',
          top: '-100px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(230, 57, 70, 0.1) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 'var(--spacing-2) var(--spacing-4)',
            backgroundColor: 'rgba(230, 57, 70, 0.1)',
            borderRadius: 'var(--radius-full)',
            marginBottom: 'var(--spacing-4)',
            animation: 'slideDown 0.6s ease-out'
          }}>
            <Sparkles size={16} color="#E63946" />
            <span style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-semibold)',
              color: '#E63946'
            }}>
              100% Free • No Registration Required
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 'var(--font-extrabold)',
            marginBottom: 'var(--spacing-4)',
            background: 'linear-gradient(135deg, #1F2937 0%, #4B5563 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: 1.2,
            animation: 'slideUp 0.6s ease-out'
          }}>
            Every tool you need to work<br />with PDFs in one place
          </h1>

          <p style={{
            fontSize: 'var(--text-lg)',
            color: 'var(--color-text-secondary)',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: 1.6,
            animation: 'slideUp 0.7s ease-out'
          }}>
            Professional PDF tools powered by AI. Merge, split, compress, convert, and edit PDFs with ease.
            <strong style={{ color: 'var(--color-text-primary)' }}> All tools are 100% free</strong> and work directly in your browser.
          </p>

          {/* Stats */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--spacing-8)',
            marginTop: 'var(--spacing-6)',
            flexWrap: 'wrap',
            animation: 'slideUp 0.8s ease-out'
          }}>
            {[
              { label: 'PDF Tools', value: '40+' },
              { label: 'Files Processed', value: '1M+' },
              { label: 'User Rating', value: '4.9★' }
            ].map((stat, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 'var(--font-bold)',
                  color: '#E63946',
                  marginBottom: 'var(--spacing-1)'
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-tertiary)',
                  fontWeight: 'var(--font-medium)'
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tool Categories */}
      {toolCategories.map((category, categoryIndex) => (
        <div
          key={category.id}
          style={{
            marginBottom: 'var(--spacing-12)',
            animation: `fadeInUp 0.6s ease-out ${categoryIndex * 0.1}s backwards`
          }}
          onMouseEnter={() => setHoveredCategory(category.id)}
          onMouseLeave={() => setHoveredCategory(null)}
        >
          {/* Category Header */}
          <div style={{ marginBottom: 'var(--spacing-6)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-2)' }}>
              <div style={{
                width: '4px',
                height: '24px',
                background: category.gradient,
                borderRadius: 'var(--radius-full)',
                transition: 'height 0.3s ease',
                ...(hoveredCategory === category.id && { height: '32px' })
              }} />
              <h2 style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-extrabold)',
                color: 'var(--color-text-tertiary)',
                letterSpacing: '0.1em',
                margin: 0
              }}>
                {category.title}
              </h2>
            </div>
            <p style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              marginLeft: 'calc(4px + var(--spacing-3))',
              fontWeight: 'var(--font-medium)'
            }}>
              {category.description}
            </p>
          </div>

          {/* Tools Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 'var(--spacing-4)'
          }}>
            {category.tools.map((tool, toolIndex) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.id}
                  onClick={() => tool.component && onSelectTool(tool.id, tool.component)}
                  disabled={!tool.component}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 'var(--spacing-3)',
                    padding: 'var(--spacing-5)',
                    backgroundColor: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-xl)',
                    cursor: tool.component ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textAlign: 'left',
                    opacity: tool.component ? 1 : 0.6,
                    overflow: 'hidden',
                    animation: `fadeInUp 0.5s ease-out ${(categoryIndex * 0.1) + (toolIndex * 0.05)}s backwards`
                  }}
                  onMouseEnter={(e) => {
                    if (tool.component) {
                      e.currentTarget.style.borderColor = category.color;
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      e.currentTarget.style.boxShadow = `0 12px 24px -10px ${category.color}40`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border)';
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Background gradient on hover */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100px',
                    height: '100px',
                    background: category.gradient,
                    opacity: 0.05,
                    borderRadius: '50%',
                    transform: 'translate(30%, -30%)',
                    pointerEvents: 'none'
                  }} />

                  {/* Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: 'var(--radius-lg)',
                    background: category.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: `0 4px 12px -4px ${category.color}60`,
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <Icon size={24} color="white" strokeWidth={2.5} />
                  </div>

                  {/* Content */}
                  <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
                    <div style={{
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-bold)',
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--spacing-1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)'
                    }}>
                      {tool.label}
                      {!tool.component && (
                        <span style={{
                          fontSize: 'var(--text-xs)',
                          padding: '2px 6px',
                          backgroundColor: 'var(--color-warning-subtle)',
                          color: 'var(--color-warning)',
                          borderRadius: 'var(--radius-sm)',
                          fontWeight: 'var(--font-semibold)'
                        }}>
                          Soon
                        </span>
                      )}
                    </div>
                    <p style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-text-tertiary)',
                      margin: 0,
                      lineHeight: 1.4
                    }}>
                      {tool.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Bottom CTA */}
      <div style={{
        marginTop: 'var(--spacing-16)',
        padding: 'var(--spacing-10)',
        background: 'linear-gradient(135deg, #E63946 0%, #FF6B6B 100%)',
        borderRadius: 'var(--radius-2xl)',
        textAlign: 'center',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        animation: 'fadeInUp 0.8s ease-out'
      }}>
        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '150px',
          height: '150px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <Zap size={48} style={{ marginBottom: 'var(--spacing-4)' }} />
          <h2 style={{
            fontSize: 'var(--text-3xl)',
            fontWeight: 'var(--font-bold)',
            marginBottom: 'var(--spacing-3)'
          }}>
            Ready to transform your PDFs?
          </h2>
          <p style={{
            fontSize: 'var(--text-lg)',
            opacity: 0.95,
            maxWidth: '600px',
            margin: '0 auto var(--spacing-6)'
          }}>
            Join millions of users who trust YoursPDF for their document needs.
            All tools are free, secure, and work directly in your browser.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              padding: 'var(--spacing-4) var(--spacing-8)',
              fontSize: 'var(--text-base)',
              fontWeight: 'var(--font-bold)',
              backgroundColor: 'white',
              color: '#E63946',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              cursor: 'pointer',
              transition: 'transform 0.2s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Get Started Now
          </button>
        </div>
      </div>

      {/* Add keyframe animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

// Tool Wrapper Component to isolate dynamic component rendering
const ToolWrapper = React.memo(function ToolWrapper({ component: Component }) {
  if (!Component) return null;
  return <Component />;
});

// Main Dashboard Component (keeping all existing functionality)
const Dashboard = React.memo(function Dashboard() {
  const [currentView, setCurrentView] = React.useState('home');
  const [currentComponent, setCurrentComponent] = React.useState(null);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showAllTools, setShowAllTools] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  // Handle scroll for navbar effect
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSelectTool = (toolId, component) => {
    setCurrentView(toolId);
    setCurrentComponent(() => component);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setCurrentComponent(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Flatten all tools for the top navigation
  const allTools = toolCategories.flatMap(cat => cat.tools);
  const topNavTools = allTools.filter(t => t.component).slice(0, 4);

  return (
    <div className="dashboard-container">
      {/* Enhanced Top Navigation Bar */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${scrolled ? 'var(--color-border)' : 'var(--color-border-light)'}`,
        zIndex: 'var(--z-fixed)',
        transition: 'all 0.3s ease',
        boxShadow: scrolled ? '0 4px 12px rgba(0, 0, 0, 0.05)' : 'none'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 var(--spacing-6)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '72px'
          }}>
            {/* Logo */}
            <a
              href="/"
              onClick={(e) => {
                if (currentView !== 'home') {
                  e.preventDefault();
                  handleBackToHome();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
                color: 'var(--color-primary)',
                fontWeight: 'var(--font-extrabold)',
                fontSize: 'var(--text-2xl)',
                fontFamily: 'var(--font-display)',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <svg width="36" height="36" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="4" width="20" height="24" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M10 10H22M10 14H22M10 18H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M16 24L16 28" stroke="#E63946" strokeWidth="2.5" strokeLinecap="round" />
                <circle cx="16" cy="28" r="2" fill="#E63946" />
              </svg>
              <span>YoursPDF</span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {topNavTools.map(tool => (
                <button
                  key={tool.id}
                  onClick={() => handleSelectTool(tool.id, tool.component)}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                    color: currentView === tool.id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                    backgroundColor: currentView === tool.id ? 'var(--color-accent-subtle)' : 'transparent',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--color-accent)';
                    if (currentView !== tool.id) {
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-elevated)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentView !== tool.id) {
                      e.currentTarget.style.color = 'var(--color-text-secondary)';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {tool.label.toUpperCase()}
                </button>
              ))}

              <button
                onClick={() => setShowAllTools(!showAllTools)}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--color-accent)',
                  backgroundColor: showAllTools ? 'var(--color-accent-subtle)' : 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-1)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => !showAllTools && (e.currentTarget.style.backgroundColor = 'var(--color-surface-elevated)')}
                onMouseLeave={(e) => !showAllTools && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                ALL PDF TOOLS
                <ChevronDown size={16} style={{
                  transform: showAllTools ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }} />
              </button>

              <button
                onClick={handleBackToHome}
                style={{
                  padding: 'var(--spacing-2)',
                  marginLeft: 'var(--spacing-2)',
                  backgroundColor: currentView === 'home' ? 'var(--color-accent-subtle)' : 'transparent',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => currentView !== 'home' && (e.currentTarget.style.backgroundColor = 'var(--color-surface-elevated)')}
                onMouseLeave={(e) => currentView !== 'home' && (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <Grid3x3 size={20} color={currentView === 'home' ? 'var(--color-accent)' : 'var(--color-text-secondary)'} />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{
                padding: 'var(--spacing-2)',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* All Tools Dropdown - Enhanced */}
        {showAllTools && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderBottom: '1px solid var(--color-border)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
            maxHeight: '80vh',
            overflowY: 'auto',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <div style={{
              maxWidth: '1400px',
              margin: '0 auto',
              padding: 'var(--spacing-8) var(--spacing-6)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--spacing-8)'
            }}>
              {toolCategories.map(category => (
                <div key={category.id}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    marginBottom: 'var(--spacing-4)'
                  }}>
                    <div style={{
                      width: '3px',
                      height: '16px',
                      background: category.gradient,
                      borderRadius: 'var(--radius-full)'
                    }} />
                    <h3 style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-extrabold)',
                      color: 'var(--color-text-tertiary)',
                      letterSpacing: '0.1em',
                      margin: 0
                    }}>
                      {category.title}
                    </h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
                    {category.tools.map(tool => {
                      const Icon = tool.icon;
                      return (
                        <button
                          key={tool.id}
                          onClick={() => {
                            if (tool.component) {
                              handleSelectTool(tool.id, tool.component);
                              setShowAllTools(false);
                            }
                          }}
                          disabled={!tool.component}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-3)',
                            padding: 'var(--spacing-3)',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: tool.component ? 'pointer' : 'not-allowed',
                            opacity: tool.component ? 1 : 0.5,
                            textAlign: 'left',
                            borderRadius: 'var(--radius-lg)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (tool.component) {
                              e.currentTarget.style.backgroundColor = 'var(--color-surface-elevated)';
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}
                        >
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: 'var(--radius-md)',
                            background: `${category.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <Icon size={16} color={category.color} />
                          </div>
                          <span style={{
                            fontSize: 'var(--text-sm)',
                            fontWeight: 'var(--font-medium)',
                            color: 'var(--color-text-primary)',
                            flex: 1
                          }}>
                            {tool.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mobile Menu - Enhanced */}
        {isMenuOpen && (
          <div className="md:hidden" style={{
            borderTop: '1px solid var(--color-border-light)',
            backgroundColor: 'white',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <div style={{
              padding: 'var(--spacing-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-2)',
              maxHeight: '70vh',
              overflowY: 'auto'
            }}>
              <button
                onClick={() => {
                  handleBackToHome();
                  setIsMenuOpen(false);
                }}
                style={{
                  padding: 'var(--spacing-4)',
                  backgroundColor: 'var(--color-accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-bold)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--spacing-2)'
                }}
              >
                <Grid3x3 size={20} />
                All Tools
              </button>
              {allTools.filter(t => t.component).slice(0, 12).map(tool => (
                <button
                  key={tool.id}
                  onClick={() => {
                    handleSelectTool(tool.id, tool.component);
                    setIsMenuOpen(false);
                  }}
                  style={{
                    padding: 'var(--spacing-3)',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-lg)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {tool.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main style={{
        paddingTop: '88px',
        minHeight: '100vh',
        backgroundColor: 'var(--color-background)'
      }}>
        {currentView === 'home' ? (
          <DashboardHome onSelectTool={handleSelectTool} />
        ) : currentComponent ? (
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: 'var(--spacing-8) var(--spacing-6)',
            animation: 'fadeIn 0.4s ease-out'
          }}>
            <ToolWrapper component={currentComponent} />
          </div>
        ) : (
          <div style={{
            padding: 'var(--spacing-12)',
            textAlign: 'center',
            animation: 'fadeIn 0.4s ease-out'
          }}>
            <div style={{
              maxWidth: '500px',
              margin: '0 auto',
              padding: 'var(--spacing-10)',
              backgroundColor: 'var(--color-surface)',
              borderRadius: 'var(--radius-2xl)',
              border: '1px solid var(--color-border)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto var(--spacing-6)',
                borderRadius: '50%',
                backgroundColor: 'var(--color-warning-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Sparkles size={40} color="var(--color-warning)" />
              </div>
              <h2 style={{
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-bold)',
                marginBottom: 'var(--spacing-3)',
                color: 'var(--color-text-primary)'
              }}>
                Coming Soon
              </h2>
              <p style={{
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-6)',
                lineHeight: 1.6
              }}>
                This tool is under development and will be available soon.
                We're working hard to bring you the best PDF tools!
              </p>
              <button
                onClick={handleBackToHome}
                style={{
                  padding: 'var(--spacing-3) var(--spacing-6)',
                  backgroundColor: 'var(--color-accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-lg)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-bold)',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Back to All Tools
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
});

// Main App with Router (unchanged)
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/app" element={<Dashboard />} />
        <Route path="/app/:tool" element={<Dashboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </BrowserRouter>
  );
}

export default App;
